import { useMemo } from "react";

import { useAuthContext } from "contexts/AuthContext";

/**
 * One place to ask what the signed-in user may do.
 *
 * Views used to compare `accountUserType === "read_only"` inline, in fifteen places
 * across eleven files, and handled the answer three different ways: disable the
 * button, hide the whole block, or branch inside a handler. Ask about the intent
 * instead of the role, the same way the server does with CanWriteAccountData.
 *
 * These are conveniences, not a security control. The server is authoritative;
 * see server/docs/2260-read-only-write-permissions.md.
 */
const usePermissions = () => {
  const { accountUserType, isAdmin } = useAuthContext();

  return useMemo(() => {
    const role = accountUserType || null;
    const isReadOnly = role === "read_only";
    return {
      role,
      isReadOnly,
      // Until the role has loaded we assume no write rights, so the UI does not
      // flash enabled buttons at a read-only user on first paint.
      canWrite: role === "admin" || role === "basic",
      canAdmin: role === "admin" || Boolean(isAdmin),
    };
  }, [accountUserType, isAdmin]);
};

export default usePermissions;
